/**
 * @file FreeModeControls.tsx
 * @brief Free card on Super Admin → Plans & seats: "Free minutes: Every
 *        month | One time" and, for one time, "Expires: No expiry | After
 *        N days" (B20 / W17, 24-10-2026)
 *
 * @details
 * FREE_PLAN_RESET_MODE (a `choice`) and FREE_PLAN_EXPIRY_DAYS (0 = never)
 * edit the page's form like every other row; nothing is saved until the
 * changes bar. BACKEND B20: the numbers saved WITH the switch belong to the
 * new mode, so the mode and the Free numbers go in one Save; the line under
 * the control says so as soon as the mode changes. Shown only when the
 * backend sends the mode (B20); an older backend keeps today's monthly Free.
 * Used on the card and in the Edit plan sheet.
 *
 * Related: SubscriptionPlansCard.tsx, PlanEditSheet.tsx, PlanChangesBar.tsx
 *
 * @author DES (prepared with Claude)
 * @date 24-10-2026
 */

import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/Tooltip";
import { cn } from "@/shared/theme/cn";
import {
  CHOICE_LABELS,
  FREE_EXPIRY_KEY,
  FREE_RESET_MODE_KEY,
  freeModeOf,
  type PlanSetting,
} from "../launchPlans";
import { fieldError, keyTip, type PlanForm } from "../planForm";
import { ChangedDot } from "./PlanSettingRow";

/** Said under the mode once it changes (the review bar says it again). */
export const FREE_TOGETHER_NOTE =
  "Save the mode and the Free numbers together: numbers saved with this switch belong to the new mode.";

function Segmented({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-full bg-black/[0.04] p-1 dark:bg-white/[0.06]">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            "rounded-full px-3 py-1 text-xs font-medium transition-colors focus-visible:outline-none",
            value === o.value ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ControlLabel({ s, form, text }: { s: PlanSetting; form: PlanForm; text: string }) {
  const bad = form.invalid.has(s.key);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <p className={cn("text-sm font-medium", bad ? "text-destructive" : "text-foreground")}>
          {bad ? fieldError(s) : text}
        </p>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[320px] break-all font-mono text-[11px]">
        {keyTip(s, form)}
      </TooltipContent>
    </Tooltip>
  );
}

export function FreeModeControls({
  setting,
  form,
  className,
}: {
  /** Any setting by key on the Free card. */
  setting: (key: string) => PlanSetting | undefined;
  form: PlanForm;
  className?: string;
}) {
  const modeSetting = setting(FREE_RESET_MODE_KEY);
  if (!modeSetting) return null;
  const expiry = setting(FREE_EXPIRY_KEY);
  const mode = freeModeOf(form.value(FREE_RESET_MODE_KEY));
  const modeChanged = form.value(FREE_RESET_MODE_KEY) !== (form.baseline[FREE_RESET_MODE_KEY] ?? "");
  const options = (modeSetting.options?.length ? modeSetting.options : ["monthly", "one_time"]).map((v) => ({
    value: v,
    label: CHOICE_LABELS[v] ?? v,
  }));

  const days = expiry ? form.value(FREE_EXPIRY_KEY).trim() : "";
  const never = days === "0";
  const expiryChanged = expiry ? days !== (form.baseline[FREE_EXPIRY_KEY] ?? "") : false;
  const before = form.baseline[FREE_EXPIRY_KEY] ?? "";

  return (
    <div className={cn("space-y-3 py-2", className)}>
      <div className="flex items-start gap-2">
        <span className="mt-2">
          <ChangedDot on={modeChanged} />
        </span>
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <ControlLabel s={modeSetting} form={form} text="Free minutes" />
            <Segmented
              label="Free minutes"
              options={options}
              value={mode}
              onChange={(v) => form.set(FREE_RESET_MODE_KEY, v)}
            />
          </div>
          <p className={cn("text-xs", modeChanged ? "font-medium text-foreground" : "text-muted-foreground")}>
            {modeChanged
              ? FREE_TOGETHER_NOTE
              : mode === "one_time"
                ? "The numbers below are a total for each person, once. After it: a plan (never charged per use)."
                : "The numbers below reset on the 1st of every month."}
          </p>
        </div>
      </div>

      {expiry && mode === "one_time" ? (
        <div className="flex items-start gap-2">
          <span className="mt-2">
            <ChangedDot on={expiryChanged} />
          </span>
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <ControlLabel s={expiry} form={form} text="Expires" />
              <Segmented
                label="Expires"
                options={[
                  { value: "never", label: "No expiry" },
                  { value: "after", label: "After days" },
                ]}
                value={never ? "never" : "after"}
                onChange={(v) =>
                  form.set(FREE_EXPIRY_KEY, v === "never" ? "0" : before !== "0" && before !== "" ? before : "")
                }
              />
            </div>
            {never ? (
              <p className="text-xs text-muted-foreground">What is left never ends.</p>
            ) : (
              <label className="flex items-center justify-end gap-1.5 text-sm">
                <span className="text-muted-foreground">After</span>
                <input
                  aria-label="Free ends after, days"
                  inputMode="numeric"
                  value={days}
                  placeholder="30"
                  onChange={(e) => form.set(FREE_EXPIRY_KEY, e.target.value)}
                  className="w-16 border-b border-foreground/30 bg-transparent py-0.5 text-right font-medium tabular-nums text-foreground outline-none focus:border-brand"
                />
                <span className="text-muted-foreground">days from sign-up</span>
              </label>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
